import {
  Fragment,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getCountries } from 'api/countries';
import { getFlights } from 'api/flights';
import { FlightStatusContext } from 'context';
import {
  countryParser,
  locationParser,
  codeChecker,
  localizeAirportNames,
  checkValidValue,
} from 'helpers';
import { Tabs, Select, /* DateInput, */ DatePicker } from 'components';
import {
  Container,
  Wrapper,
  StyledButton,
  // Required,
} from './FormWrapper.style';
import { Input } from 'components/Select/Select.style';

const FormWrapper = ({ setIsSearch, isSearch }) => {
  const { t, i18n } = useTranslation();
  const {
    state: {
      countries,
      arrivalStation,
      departureStation,
      date,
      flightNumber,
      get_flights_list_status,
      parsedDestinations,
      flights,
      searchedDate,
    },
    dispatch,
  } = useContext(FlightStatusContext);
  const [activeTab, setActiveTab] = useState(0);
  const [flightNumberError, setFlightNumberError] = useState(false);
  const { search } = useLocation();
  const history = useHistory();

  const searchParams = useMemo(
    () =>
      search
        .slice(1)
        .split('&')
        .reduce(
          (acc, cur) => ({ ...acc, [cur.split('=')[0]]: cur.split('=')[1] }),
          {}
        ),
    [search]
  );

  const handleChangeState = useCallback(
    (value) => {
      dispatch({
        type: 'HANDLE_CHANGE_STATE',
        payload: value,
      });
    },
    [dispatch]
  );

  // Smooth scroll after search
  useEffect(() => {
    if (['success', 'error'].includes(get_flights_list_status)) {
      const container = document.getElementById('flight-results');
      if (container) container.scrollIntoView({ behavior: 'smooth' });
    }
  }, [get_flights_list_status]);

  // Parsed countries for the select input
  const parsedCountries = useMemo(() => countryParser(countries), [countries]);
  // Added parsed countries to context
  useEffect(
    () => handleChangeState({ parsedDestinations: parsedCountries }),
    [handleChangeState, parsedCountries]
  );

  // Localizing names
  const localizeAirports = useMemo(() => {
    if (
      i18n?.language !== 'en' &&
      flights?.appendix?.airports &&
      parsedDestinations
    )
      return localizeAirportNames(
        flights.appendix.airports,
        parsedDestinations
      );
    else return flights?.appendix?.airports;
  }, [flights, parsedDestinations, i18n]);

  useEffect(
    () => handleChangeState({ airports: localizeAirports }),
    [handleChangeState, localizeAirports]
  );

  // Get countries list
  const fetchCountries = useCallback(async () => {
    dispatch({ type: 'GET_COUNTRIES_LOADING' });
    const host = window.location.host.slice(-2);
    try {
      const data = await getCountries(host);
      if (data?.countries?.length > 0) {
        dispatch({
          type: 'GET_COUNTRIES_SUCCESS',
          payload: data.countries,
        });
      }
    } catch (err) {
      dispatch({ type: 'GET_COUNTRIES_ERROR' });
    }
  }, [dispatch]);

  useEffect(() => fetchCountries(), [fetchCountries]);

  // Get searched list
  const getFlightsList = useCallback(
    async (values) => {
      dispatch({ type: 'GET_FLIGHTS_LIST_LOADING' });
      try {
        const fetched_flights = await getFlights(values);
        dispatch({
          type: 'GET_FLIGHTS_LIST_SUCCESS',
          payload: fetched_flights,
        });
      } catch (err) {
        dispatch({ type: 'GET_FLIGHTS_LIST_ERROR', payload: {} });
      }
    },
    [dispatch]
  );

  // Getting searched params
  const searchedParams = useMemo(() => {
    if (searchParams && parsedCountries?.length > 0) {
      const values = {
        ...searchParams,
      };

      if (searchParams?.ssm) {
        localStorage.setItem(
          'ssm',
          searchParams?.ssm === 'true' ? true : false
        );
        values.ssm = searchParams?.ssm;
      }

      const state = {};

      if (!searchParams.date)
        state.date = {
          string: moment().format('ddd DD MMM'),
          value: moment().format('YYYY-MM-DD'),
        };

      const setDestination = (name, code) => {
        const dest = locationParser(parsedCountries, code);
        state[name] = {
          name: `${dest?.city_name} ${dest?.airport_code || dest?.city_code}`,
          codes: code,
        };
        values[name] = code;
      };

      if (
        searchParams?.arrivalStation &&
        searchParams?.arrivalStation !== 'undefined'
      )
        setDestination('arrivalStation', searchParams?.arrivalStation);

      if (
        searchParams?.departureStation &&
        searchParams?.departureStation !== 'undefined'
      )
        setDestination('departureStation', searchParams?.departureStation);

      if (!values.arrivalStation && !values.departureStation) {
        const codes = {
          se: 'ARN',
          no: 'OSL',
          dk: 'CPH',
        };
        setDestination(
          'departureStation',
          codes[window.location.hostname.slice(-2)] || 'ARN'
        );
      }

      if (!searchParams?.flightNumber) setActiveTab(0);
      else setActiveTab(1);

      if (
        searchParams?.flightNumber &&
        searchParams?.flightNumber !== 'undefined'
      ) {
        values.flightNumber = searchParams?.flightNumber;
        state.flightNumber = searchParams?.flightNumber;
      }

      return {
        values,
        state,
        flight: searchParams?.flight,
        ssm: searchParams?.ssm,
      };
    } else {
      return {};
    }
  }, [searchParams, parsedCountries]);

  useEffect(() => {
    if (searchedParams?.flight)
      dispatch({
        type: 'HANDLE_MODAL',
        payload: {
          open: true,
          id: searchedParams.flight,
        },
      });
  }, [searchedParams.flight, dispatch]);

  useEffect(
    () => handleChangeState(searchedParams.state),
    [handleChangeState, searchedParams.state]
  );

  useEffect(() => {
    if (searchedParams?.values) {
      const body = {
        arrival: searchedParams.values.arrivalStation || false,
        departure: searchedParams.values.departureStation || false,
        number: searchedParams.values.flightNumber || false,
        date: searchedParams.values.date || false,
      };
      setIsSearch(body);
      if (
        searchedParams.values.prefill !== 'true' &&
        (searchParams.arrivalStation ||
          searchParams.departureStation ||
          searchParams.flightNumber)
      ) {
        getFlightsList(searchedParams?.values);
      }
    }
  }, [searchedParams.values, setIsSearch, searchParams, getFlightsList]);

  // Setting searched values to context
  useEffect(() => {
    if (parsedDestinations?.length > 0) {
      const { arrival, departure, number, date } = isSearch;
      let departureLocation,
        arrivalLocation = {};

      if (departure && !number)
        departureLocation = locationParser(parsedDestinations, departure);
      if (arrival && !number)
        arrivalLocation = locationParser(parsedDestinations, arrival);
      if (number && flights?.flightStatuses?.length > 0) {
        const flight = flights.flightStatuses[0];
        departureLocation = locationParser(
          parsedDestinations,
          flight.departureAirportFsCode
        );
        arrivalLocation = locationParser(
          parsedDestinations,
          flight.arrivalAirportFsCode
        );
      }

      handleChangeState({
        searchedArrival: arrivalLocation,
        searchedDeparture: departureLocation,
        searchedDate: moment(date).format('MMM DD YYYY'),
      });
    }
  }, [parsedDestinations, isSearch, handleChangeState, flights]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const prevRoute = history.location.search;
    const params = searchParams;
    delete params.flightNumber;
    delete params.arrivalStation;
    delete params.departureStation;
    delete params.date;
    let block = false;

    if (activeTab === 1 && !flightNumber) setFlightNumberError(true);
    else {
      let queries = '&';

      Object.keys(params).forEach((key) => {
        queries += key && `${key}=${params[key]}&`;
      });

      if (activeTab === 1 && flightNumber) {
        setFlightNumberError(false);
        if (Number(flightNumber.slice(0, 1)))
          history.push(
            `${process.env.PUBLIC_URL}?date=${date.value}&flightNumber=SK${flightNumber}${queries}`
          );
        else
          history.push(
            `${process.env.PUBLIC_URL}?date=${date.value}&flightNumber=${flightNumber}${queries}`
          );
      }

      if (
        activeTab === 0 &&
        (checkValidValue(departureStation, t) ||
          checkValidValue(arrivalStation, t))
      ) {
        const departureCode = codeChecker(departureStation, t);
        const arrivalCode = codeChecker(arrivalStation, t);

        history.push(
          `${process.env.PUBLIC_URL}?date=${date.value}&departureStation=${departureCode}&arrivalStation=${arrivalCode}${queries}`
        );
      } else if (activeTab === 0) {
        block = true;
      }

      // forced search if clicked without input changes
      if (
        !block &&
        prevRoute === history.location.search &&
        (prevRoute.includes('flightNumber') ||
          prevRoute.includes('departureStation') ||
          prevRoute.includes('arrivalStation'))
      )
        getFlightsList(searchedParams?.values);
    }
  };

  const handleChange = useCallback(
    (value) => handleChangeState(value),
    [handleChangeState]
  );

  const ssm = useMemo(() => new URLSearchParams(search), [search]).get('ssm');

  return (
    <Container>
      <Fragment>
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
        <Wrapper onSubmit={handleSubmit} noValidate>
          {activeTab === 0 ? (
            <Fragment>
              <Select
                content={parsedCountries}
                selected={departureStation}
                setSelected={handleChange}
                label={t('flightstatus.search.origin')}
                name="departureStation"
              />
              <Select
                content={parsedCountries}
                selected={arrivalStation}
                setSelected={handleChange}
                label={t('flightstatus.search.destination')}
                name="arrivalStation"
              />
            </Fragment>
          ) : (
            <Input error={flightNumberError.toString()}>
              <TextField
                autoComplete="off"
                fullWidth
                required={activeTab === 1}
                name="flightNumber"
                value={flightNumber}
                error={flightNumberError}
                onChange={(e) =>
                  handleChange({ [e.target.name]: e.target.value })
                }
                variant="standard"
                label={t('flightstatus.search.flightnumber')}
                InputProps={{
                  disableUnderline: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Input>
          )}
          <DatePicker
            searched={searchedDate}
            date={date}
            handleChange={handleChange}
            ssm={ssm}
          />
          <StyledButton type="submit" color="secondary" variant="contained">
            {t('flightstatus.search.button')}
          </StyledButton>
        </Wrapper>
      </Fragment>
    </Container>
  );
};

export default FormWrapper;
